import { useState, useEffect } from 'react'
import { useClientStore, selectCurrentClient } from '../../stores/clientStore'
import { useCanAccess } from '../../hooks/useCanAccess'
import { clientApi } from '../../services/clientApi'
import UserGroupAssignment from './UserGroupAssignment'
import type { ClientUser } from '../../types/client'

export default function UserManagement() {
  const currentClient = useClientStore(selectCurrentClient)
  const clients = useClientStore((s) => s.clients)
  const currentClientId = useClientStore((s) => s.currentClientId)
  const setCurrentClient = useClientStore((s) => s.setCurrentClient)
  const canCreateUser = useCanAccess('users', 'create')
  const canDeleteUser = useCanAccess('users', 'delete')
  const canUpdateUser = useCanAccess('users', 'update')

  const [users, setUsers] = useState<ClientUser[]>([])
  const [loading, setLoading] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [newUser, setNewUser] = useState({ email: '', first_name: '', last_name: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [groupsUserId, setGroupsUserId] = useState<string | null>(null)

  const loadUsers = async (clientId: string) => {
    setLoading(true)
    try {
      const data = await clientApi.getClientUsers(clientId)
      setUsers(data)
    } catch {
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (currentClientId) loadUsers(currentClientId)
  }, [currentClientId])

  const handleCreate = async () => {
    if (!currentClientId) return
    setSaving(true)
    setError(null)
    try {
      await clientApi.createClientUser(currentClientId, newUser)
      setShowCreate(false)
      setNewUser({ email: '', first_name: '', last_name: '' })
      await loadUsers(currentClientId)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur creation')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (user: ClientUser) => {
    if (!currentClientId) return
    if (!confirm(`Supprimer l'utilisateur "${user.email}" ?`)) return
    try {
      await clientApi.deleteClientUser(currentClientId, user.id)
      await loadUsers(currentClientId)
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Erreur suppression')
    }
  }

  return (
    <div className="admin-section">
      <div className="admin-section__header">
        <h3>Utilisateurs</h3>
        <div className="admin-section__actions">
          <select
            className="site-selector"
            value={currentClientId ?? ''}
            onChange={(e) => setCurrentClient(e.target.value || null)}
          >
            <option value="">-- Client --</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {currentClientId && canCreateUser && (
            <button className="admin-btn admin-btn--create" onClick={() => setShowCreate(true)}>
              Nouvel utilisateur
            </button>
          )}
        </div>
      </div>

      {!currentClient && (
        <p className="admin-loading">Selectionnez un client pour voir ses utilisateurs.</p>
      )}

      {loading && <p className="admin-loading">Chargement...</p>}

      {currentClient && !loading && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Nom</th>
              <th>Groupes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.email}</td>
                <td>{u.first_name} {u.last_name}</td>
                <td>
                  {u.groups.map((g) => (
                    <span key={g} className="admin-badge admin-badge--blue">{g}</span>
                  ))}
                </td>
                <td className="admin-table__actions">
                  {canUpdateUser && (
                    <button className="admin-btn admin-btn--small" onClick={() => setGroupsUserId(u.id)}>
                      Permissions
                    </button>
                  )}
                  {canDeleteUser && (
                    <button className="admin-btn admin-btn--small admin-btn--danger" onClick={() => handleDelete(u)}>
                      Supprimer
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={4} className="admin-table__empty">Aucun utilisateur</td></tr>
            )}
          </tbody>
        </table>
      )}

      {/* Create user modal */}
      {showCreate && (
        <div className="admin-modal__overlay" onClick={() => setShowCreate(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Nouvel utilisateur</h3>
            <div className="admin-form">
              <div className="admin-form__field">
                <label>Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser((p) => ({ ...p, email: e.target.value }))}
                />
              </div>
              <div className="admin-form__field">
                <label>Prenom</label>
                <input
                  type="text"
                  value={newUser.first_name}
                  onChange={(e) => setNewUser((p) => ({ ...p, first_name: e.target.value }))}
                />
              </div>
              <div className="admin-form__field">
                <label>Nom</label>
                <input
                  type="text"
                  value={newUser.last_name}
                  onChange={(e) => setNewUser((p) => ({ ...p, last_name: e.target.value }))}
                />
              </div>
              {error && <div className="admin-error">{error}</div>}
              <div className="admin-form__actions">
                <button className="admin-btn" onClick={() => setShowCreate(false)}>Annuler</button>
                <button className="admin-btn admin-btn--create" onClick={handleCreate} disabled={saving}>
                  {saving ? 'Creation...' : 'Creer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Groups assignment modal */}
      {groupsUserId && currentClientId && (
        <UserGroupAssignment
          clientId={currentClientId}
          userId={groupsUserId}
          onClose={() => {
            setGroupsUserId(null)
            if (currentClientId) loadUsers(currentClientId)
          }}
        />
      )}
    </div>
  )
}
